<template>
  <div class="matrix-pricing-editor" tabindex="-1" @paste="handlePaste">
    <div class="matrix-content">
      <!-- Dimension Setup -->
      <div class="row" :class="{ 'matrix-dimensions--locked': lockDimensions }">
        <div class="col-lg">
          <div class="dimension-setup card">
            <div class="dimension-selector">
              <SelectInput
                id="row-dimension"
                label="Rij dimensie"
                v-model="dimensions.row"
                :options="availableOptionsList"
                placeholder="Bijv. Uitval (mm)"
              />
              <NumberInput
                v-if="dimensions.row && isNumberOption(dimensions.row)"
                id="row-matrix-step"
                label="Matrix stap"
                v-model="matrixSteps.row"
                :min="1"
                :placeholder="getOptionStep(dimensions.row)"
                help="Stapgrootte voor de matrix (bijv. 100 voor 2000, 2100, 2200...)"
              />
              <NumberInput
                v-if="dimensions.row && isNumberOption(dimensions.row)"
                id="row-matrix-start"
                label="Matrix start"
                v-model="matrixStarts.row"
                :placeholder="getOptionMin(dimensions.row)"
                help="Eerste waarde in de matrix (optioneel). Bijv. 1000 terwijl het veld vanaf 670 mag; matrix wordt dan 1000, 1200, 1400..."
              />
              <div v-if="dimensions.row" class="dimension-preview">
                <small>
                  <strong>Matrix waarden:</strong>
                  <span v-if="getMatrixDimensionPreview(dimensions.row, matrixSteps.row, matrixStarts.row).length > 0">
                    {{ getMatrixDimensionPreview(dimensions.row, matrixSteps.row, matrixStarts.row).join(', ') }}
                    <span v-if="getMatrixDimensionValues(dimensions.row, matrixSteps.row, matrixStarts.row).length > 5">
                      ({{ getMatrixDimensionValues(dimensions.row, matrixSteps.row, matrixStarts.row).length }} waarden)
                    </span>
                  </span>
                  <span v-else class="text-muted">Geen waarden beschikbaar</span>
                </small>
              </div>
            </div>
          </div>
        </div>
        <div class="col-lg">
          <div class="dimension-setup card">
            <div class="dimension-selector">
              <SelectInput
                id="col-dimension"
                label="Kolom dimensie"
                v-model="dimensions.col"
                :options="availableOptionsList"
                placeholder="Bijv. Breedte (mm)"
              />
              <NumberInput
                v-if="dimensions.col && isNumberOption(dimensions.col)"
                id="col-matrix-step"
                label="Matrix stap"
                v-model="matrixSteps.col"
                :min="1"
                :placeholder="getOptionStep(dimensions.col)"
                help="Stapgrootte voor de matrix (bijv. 100 voor 1000, 1100, 1200...)"
              />
              <NumberInput
                v-if="dimensions.col && isNumberOption(dimensions.col)"
                id="col-matrix-start"
                label="Matrix start"
                v-model="matrixStarts.col"
                :placeholder="getOptionMin(dimensions.col)"
                help="Eerste waarde in de matrix (optioneel). Bijv. 1000 terwijl het veld vanaf 670 mag."
              />
              <div v-if="dimensions.col" class="dimension-preview">
                <small>
                  <strong>Matrix waarden:</strong>
                  <span v-if="getMatrixDimensionPreview(dimensions.col, matrixSteps.col, matrixStarts.col).length > 0">
                    {{ getMatrixDimensionPreview(dimensions.col, matrixSteps.col, matrixStarts.col).join(', ') }}
                    <span v-if="getMatrixDimensionValues(dimensions.col, matrixSteps.col, matrixStarts.col).length > 5">
                      ({{ getMatrixDimensionValues(dimensions.col, matrixSteps.col, matrixStarts.col).length }} waarden)
                    </span>
                  </span>
                  <span v-else class="text-muted">Geen waarden beschikbaar</span>
                </small>
              </div>
            </div>
          </div>
        </div>
        <div class="col-lg-12">
          <div class="third-dimension card">
            <ToggleInput
              id="use-third-dimension"
              label="Extra dimensie toevoegen (bijv. Type Doek)"
              v-model="useThirdDimension"
            />

            <div v-if="useThirdDimension" class="dimension-selector">
              <SelectInput
                id="group-dimension"
                label="Groep dimensie"
                v-model="dimensions.group"
                :options="availableOptionsList"
                placeholder="Bijv. Type Doek"
              />
              <NumberInput
                v-if="dimensions.group && isNumberOption(dimensions.group)"
                id="group-matrix-step"
                label="Matrix stap"
                v-model="matrixSteps.group"
                :min="1"
                :placeholder="getOptionStep(dimensions.group)"
                help="Stapgrootte voor de matrix"
              />
              <NumberInput
                v-if="dimensions.group && isNumberOption(dimensions.group)"
                id="group-matrix-start"
                label="Matrix start"
                v-model="matrixStarts.group"
                :placeholder="getOptionMin(dimensions.group)"
                help="Eerste waarde in de matrix (optioneel)."
              />
              <div v-if="dimensions.group" class="dimension-preview">
                <small>
                  <strong>Matrix waarden:</strong>
                  <span v-if="getMatrixDimensionPreview(dimensions.group, matrixSteps.group, matrixStarts.group).length > 0">
                    {{ getMatrixDimensionPreview(dimensions.group, matrixSteps.group, matrixStarts.group).join(', ') }}
                    <span v-if="getMatrixDimensionValues(dimensions.group, matrixSteps.group, matrixStarts.group).length > 5">
                      ({{ getMatrixDimensionValues(dimensions.group, matrixSteps.group, matrixStarts.group).length }} waarden)
                    </span>
                  </span>
                  <span v-else class="text-muted">Geen waarden beschikbaar</span>
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Generate Grid Button -->
      <div class="row">
        <div class="col-lg-12">
          <div class="grid-actions-section">
            <Btn
              variant="primary"
              @click="generateGrid"
              type="button"
              :disabled="!dimensions.row || !dimensions.col"
              icon="grid"
            >
              Matrix genereren
            </Btn>

            <Btn
              variant="secondary"
              @click="openCsvImport"
              type="button"
              :disabled="!dimensions.row || !dimensions.col"
              icon="upload"
            >
              Import CSV
            </Btn>

            <span v-if="dimensions.row && dimensions.col" class="paste-hint text-muted">
              of plak (Cmd/Ctrl+V) direct data uit Excel of Google Sheets
            </span>

            <div v-if="priceGrid.length > 0" class="grid-stats">
              <span class="stat">
                {{ priceGrid.length }} rijen × {{ getColumnValues().length }} kolommen =
                {{ priceGrid.length * getColumnValues().length }} cellen
              </span>
            </div>
          </div>
        </div>
      </div>

      <div v-if="axisRangeIssues.length > 0" class="axis-range-warning">
        <strong>De matrix past niet bij het bereik van de opties</strong>
        <ul>
          <li v-for="issue in axisRangeIssues" :key="issue.optionId">{{ issue.message }}</li>
        </ul>
        <template v-if="canSwapAxes && !lockDimensions">
          <p>
            De rijwaarden passen precies bij de kolomoptie en andersom. Waarschijnlijk zijn
            rij en kolom omgedraaid.
          </p>
          <Btn variant="secondary" size="sm" type="button" @click="swapAxes">
            Assen omwisselen
          </Btn>
        </template>
      </div>

      <!-- Price Grid -->
      <div v-if="priceGrid.length > 0" class="price-grid-section card card--price-matrix">
        <div class="section-header">
          <h4>Prijsmatrix</h4>
          <div class="grid-actions">
            <Btn variant="text" @click="exportToCsv" icon="download" size="sm" type="button">
              Export CSV
            </Btn>
            <Btn variant="text" @click="openCsvImport" icon="upload" size="sm" type="button">
              Import CSV
            </Btn>
            <Btn variant="secondary" @click="fillEmptyCells" size="sm" type="button">
              Vul lege cellen
            </Btn>
          </div>
        </div>

        <div class="matrix-table-wrapper">
          <table class="matrix-table">
            <thead>
              <tr>
                <th v-if="useThirdDimension">{{ getOptionLabel(dimensions.group) }}</th>
                <th>{{ getOptionLabel(dimensions.row) }}</th>
                <th v-for="colValue in getColumnValues()" :key="colValue" class="col-header">
                  {{ axisLabel(dimensions.col, colValue) }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, rowIdx) in priceGrid" :key="rowIdx" class="grid-row">
                <td v-if="useThirdDimension" class="group-cell">
                  {{ axisLabel(dimensions.group, row.groupValue) || '-' }}
                </td>
                <td class="row-cell">{{ axisLabel(dimensions.row, row.rowValue) }}</td>
                <td v-for="(price, colIdx) in row.prices" :key="colIdx" class="price-cell">
                  <input
                    v-model.number="row.prices[colIdx]"
                    type="number"
                    step="0.01"
                    min="0"
                    class="price-input"
                    placeholder="0.00"
                    :data-row="rowIdx"
                    :data-col="colIdx"
                    @input="markCellAsEdited(rowIdx, colIdx)"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="grid-help">
          <p v-if="manualColumns" class="text-muted">
            <strong>Aangepaste kolommen actief:</strong>
            de kolomwaarden komen uit de geïmporteerde CSV (onregelmatige stappen
            toegestaan). "Matrix genereren" overschrijft deze met gelijkmatige stappen.
          </p>
          <p class="text-muted">
            <strong>Tip:</strong>
            Gebruik "Vul lege cellen" om automatisch prijzen in te vullen op basis van patronen.
          </p>
          <p class="text-muted">
            <strong>Plakken:</strong>
            Plak (Cmd/Ctrl+V) in een cel om een rij of blok waarden vanaf die cel in te vullen.
            Plak buiten de cellen (bijv. op de titel) om de hele matrix te vervangen, inclusief
            kopregel met kolomwaarden.
          </p>
        </div>
      </div>

      <!-- Lookup Delta Tables -->
      <div class="delta-section card">
        <div class="section-header">
          <h4>Lookup Delta tabellen</h4>
          <Btn variant="secondary" size="sm" icon="plus" type="button" @click="addDeltaTable">
            Delta tabel toevoegen
          </Btn>
        </div>

        <p class="text-muted">
          Gebruik delta tabellen voor prijsverschillen bovenop de basis matrix (bijv. motor, support
          beam, mainhousing).
        </p>

        <div v-if="deltaTables.length === 0" class="delta-empty text-muted">
          Nog geen delta tabellen toegevoegd.
        </div>

        <div v-else class="delta-list">
          <div v-for="(table, tableIdx) in deltaTables" :key="table.id || tableIdx" class="delta-card">
            <div class="section-header">
              <h5>Delta tabel {{ tableIdx + 1 }}</h5>
              <div class="grid-actions">
                <ToggleInput :id="`delta-active-${tableIdx}`" label="Actief" v-model="table.active" />
                <Btn
                  variant="outlined"
                  size="sm"
                  icon="trash"
                  type="button"
                  @click="removeDeltaTable(tableIdx)"
                >
                  Verwijder
                </Btn>
              </div>
            </div>

            <div class="delta-card__meta">
              <TextInput
                :id="`delta-label-${tableIdx}`"
                label="Delta label"
                v-model="table.label"
                placeholder="Bijv. Motor delta"
              />
              <NumberInput
                :id="`delta-default-${tableIdx}`"
                label="Fallback waarde"
                v-model="table.defaultValue"
                :step="0.01"
              />
            </div>

            <div class="row">
              <div class="col-lg">
                <div class="dimension-setup card">
                  <div class="dimension-selector">
                    <SelectInput
                      :id="`delta-row-${tableIdx}`"
                      label="Rij dimensie"
                      v-model="table.dimensions.row"
                      :options="availableOptionsList"
                      placeholder="Bijv. Motor"
                    />
                    <NumberInput
                      v-if="table.dimensions.row && isNumberOption(table.dimensions.row)"
                      :id="`delta-row-step-${tableIdx}`"
                      label="Matrix stap"
                      v-model="table.matrixSteps.row"
                      :min="1"
                      :placeholder="getOptionStep(table.dimensions.row)"
                    />
                    <NumberInput
                      v-if="table.dimensions.row && isNumberOption(table.dimensions.row)"
                      :id="`delta-row-start-${tableIdx}`"
                      label="Matrix start"
                      v-model="table.matrixStarts.row"
                      :placeholder="getOptionMin(table.dimensions.row)"
                    />
                    <div v-if="table.dimensions.row" class="dimension-preview">
                      <small>
                        <strong>Matrix waarden:</strong>
                        <span
                          v-if="
                            getMatrixDimensionPreview(
                              table.dimensions.row,
                              table.matrixSteps.row,
                              table.matrixStarts.row
                            ).length > 0
                          "
                        >
                          {{
                            getMatrixDimensionPreview(
                              table.dimensions.row,
                              table.matrixSteps.row,
                              table.matrixStarts.row
                            ).join(', ')
                          }}
                        </span>
                        <span v-else class="text-muted">Geen waarden beschikbaar</span>
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-lg">
                <div class="dimension-setup card">
                  <div class="dimension-selector">
                    <SelectInput
                      :id="`delta-col-${tableIdx}`"
                      label="Kolom dimensie (optioneel)"
                      v-model="table.dimensions.col"
                      :options="[{ value: '', label: 'Geen kolom dimensie' }, ...availableOptionsList]"
                      placeholder="Bijv. Breedte (mm)"
                    />
                    <NumberInput
                      v-if="table.dimensions.col && isNumberOption(table.dimensions.col)"
                      :id="`delta-col-step-${tableIdx}`"
                      label="Matrix stap"
                      v-model="table.matrixSteps.col"
                      :min="1"
                      :placeholder="getOptionStep(table.dimensions.col)"
                    />
                    <NumberInput
                      v-if="table.dimensions.col && isNumberOption(table.dimensions.col)"
                      :id="`delta-col-start-${tableIdx}`"
                      label="Matrix start"
                      v-model="table.matrixStarts.col"
                      :placeholder="getOptionMin(table.dimensions.col)"
                    />
                    <div v-if="table.dimensions.col" class="dimension-preview">
                      <small>
                        <strong>Matrix waarden:</strong>
                        <span
                          v-if="
                            getMatrixDimensionPreview(
                              table.dimensions.col,
                              table.matrixSteps.col,
                              table.matrixStarts.col
                            ).length > 0
                          "
                        >
                          {{
                            getMatrixDimensionPreview(
                              table.dimensions.col,
                              table.matrixSteps.col,
                              table.matrixStarts.col
                            ).join(', ')
                          }}
                        </span>
                        <span v-else class="text-muted">Geen waarden beschikbaar</span>
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-lg-12">
                <div class="third-dimension card">
                  <ToggleInput
                    :id="`delta-use-third-dimension-${tableIdx}`"
                    label="Extra dimensie toevoegen"
                    v-model="table.useThirdDimension"
                  />

                  <div v-if="table.useThirdDimension" class="dimension-selector">
                    <SelectInput
                      :id="`delta-group-${tableIdx}`"
                      label="Groep dimensie"
                      v-model="table.dimensions.group"
                      :options="availableOptionsList"
                      placeholder="Bijv. Type Doek"
                    />
                    <NumberInput
                      v-if="table.dimensions.group && isNumberOption(table.dimensions.group)"
                      :id="`delta-group-step-${tableIdx}`"
                      label="Matrix stap"
                      v-model="table.matrixSteps.group"
                      :min="1"
                      :placeholder="getOptionStep(table.dimensions.group)"
                    />
                    <NumberInput
                      v-if="table.dimensions.group && isNumberOption(table.dimensions.group)"
                      :id="`delta-group-start-${tableIdx}`"
                      label="Matrix start"
                      v-model="table.matrixStarts.group"
                      :placeholder="getOptionMin(table.dimensions.group)"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div class="grid-actions-section">
              <Btn
                variant="primary"
                size="sm"
                type="button"
                icon="grid"
                :disabled="!table.dimensions.row"
                @click="generateDeltaGrid(tableIdx)"
              >
                Matrix genereren
              </Btn>
              <Btn
                variant="secondary"
                size="sm"
                type="button"
                icon="upload"
                :disabled="!table.dimensions.row"
                @click="openDeltaCsvImport(tableIdx)"
              >
                Import CSV
              </Btn>
              <div v-if="table.grid && table.grid.length > 0" class="grid-stats">
                <span class="stat">
                  {{ table.grid.length }} rijen × {{ getDeltaColumnValues(table).length }} kolommen =
                  {{ table.grid.length * getDeltaColumnValues(table).length }} cellen
                </span>
              </div>
            </div>

            <div v-if="table.grid && table.grid.length > 0" class="price-grid-section card card--price-matrix">
              <div class="section-header">
                <h4>{{ table.label || `Delta tabel ${tableIdx + 1}` }}</h4>
                <div class="grid-actions">
                  <Btn
                    variant="text"
                    @click="exportDeltaToCsv(tableIdx)"
                    icon="download"
                    size="sm"
                    type="button"
                  >
                    Export CSV
                  </Btn>
                  <Btn
                    variant="text"
                    @click="openDeltaCsvImport(tableIdx)"
                    icon="upload"
                    size="sm"
                    type="button"
                  >
                    Import CSV
                  </Btn>
                  <Btn
                    variant="secondary"
                    @click="fillEmptyDeltaCells(tableIdx)"
                    size="sm"
                    type="button"
                  >
                    Vul lege cellen
                  </Btn>
                </div>
              </div>

              <div class="matrix-table-wrapper">
                <table class="matrix-table delta-table">
                  <thead>
                    <tr>
                      <th v-if="table.useThirdDimension && table.dimensions.group">
                        {{ getOptionLabel(table.dimensions.group) }}
                      </th>
                      <th>{{ getOptionLabel(table.dimensions.row) }}</th>
                      <th
                        v-for="(colValue, colIdx) in getDeltaColumnValues(table)"
                        :key="`delta-col-${tableIdx}-${colIdx}`"
                        class="col-header"
                      >
                        {{ table.dimensions.col ? axisLabel(table.dimensions.col, colValue) : 'Delta waarde' }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, rowIdx) in table.grid" :key="`delta-row-${tableIdx}-${rowIdx}`">
                      <td v-if="table.useThirdDimension && table.dimensions.group" class="group-cell">
                        {{ axisLabel(table.dimensions.group, row.groupValue) || '-' }}
                      </td>
                      <td class="row-cell">{{ axisLabel(table.dimensions.row, row.rowValue) }}</td>
                      <td
                        v-for="(value, colIdx) in row.values"
                        :key="`delta-value-${tableIdx}-${rowIdx}-${colIdx}`"
                        class="price-cell"
                      >
                        <input
                          v-model.number="row.values[colIdx]"
                          type="number"
                          step="0.01"
                          class="price-input"
                          placeholder="0.00"
                          :data-table="tableIdx"
                          :data-row="rowIdx"
                          :data-col="colIdx"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div class="grid-help">
                <p v-if="table.manualColumns" class="text-muted">
                  <strong>Aangepaste kolommen actief:</strong>
                  de kolomwaarden komen uit de geïmporteerde CSV (onregelmatige stappen
                  toegestaan). "Matrix genereren" overschrijft deze met gelijkmatige stappen.
                </p>
                <p class="text-muted">
                  <strong>Tip:</strong>
                  Gebruik "Vul lege cellen" om ontbrekende delta-waardes snel te vullen.
                </p>
                <p class="text-muted">
                  <strong>Plakken:</strong>
                  Plak (Cmd/Ctrl+V) in een cel om een rij of blok waarden vanaf die cel in te
                  vullen, of buiten de cellen om de hele tabel te vervangen (met kopregel).
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Test Calculator -->
       <span class="matrix-pricing-editor__test-calculator" @click="testingCalculator = !testingCalculator">Test calculator</span>
      <div v-if="priceGrid.length > 0 && testingCalculator" class="test-calculator card">
        <h4>Test Calculator</h4>
        <p class="text-muted mb-3">Test of de matrix pricing correct werkt</p>

        <div class="test-inputs">
          <div v-if="useThirdDimension" class="test-input">
            <SelectInput
              :id="`test-${dimensions.group}`"
              :label="getOptionLabel(dimensions.group)"
              v-model="testValues[dimensions.group]"
              :options="getDimensionChoices(dimensions.group)"
            />
          </div>

          <div class="test-input">
            <NumberInput
              v-if="getOptionType(dimensions.row) === 'number'"
              :id="`test-${dimensions.row}`"
              :label="getOptionLabel(dimensions.row)"
              v-model="testValues[dimensions.row]"
            />
            <SelectInput
              v-else
              :id="`test-${dimensions.row}`"
              :label="getOptionLabel(dimensions.row)"
              v-model="testValues[dimensions.row]"
              :options="getDimensionChoices(dimensions.row)"
            />
          </div>

          <div class="test-input">
            <NumberInput
              v-if="getOptionType(dimensions.col) === 'number'"
              :id="`test-${dimensions.col}`"
              :label="getOptionLabel(dimensions.col)"
              v-model="testValues[dimensions.col]"
            />
            <SelectInput
              v-else
              :id="`test-${dimensions.col}`"
              :label="getOptionLabel(dimensions.col)"
              v-model="testValues[dimensions.col]"
              :options="getDimensionChoices(dimensions.col)"
            />
          </div>
        </div>

        <Btn variant="primary" @click="calculateTestPrice" class="mt-3" type="button">
          Bereken Prijs
        </Btn>

        <div v-if="testResult" class="test-result mt-4">
          <div v-if="testResult.available" class="result-success">
            <h5>✅ Prijs Berekend</h5>
            <div class="result-price">€{{ testResult.price.toFixed(2) }}</div>

            <div
              v-if="testResult.tierAdjustments && testResult.tierAdjustments.length > 0"
              class="tier-adjustments"
            >
              <strong>Afrondingen:</strong>
              <ul>
                <li v-for="adj in testResult.tierAdjustments" :key="adj.dimension">
                  {{ adj.dimension }}: {{ adj.original }} → {{ adj.rounded }}
                </li>
              </ul>
            </div>

            <div class="matched-cell">
              <strong>Gebruikte cel:</strong>
              <code>{{ formatDimensionValues(testResult.dimensionValues) }}</code>
            </div>
          </div>

          <div v-else class="result-error">
            <h5>❌ Combinatie Niet Beschikbaar</h5>
            <p>{{ testResult.error }}</p>
            <div v-if="testResult.dimensionValues" class="tried-values">
              <strong>Gezochte waarden:</strong>
              <code>{{ formatDimensionValues(testResult.dimensionValues) }}</code>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { ref, computed, reactive, watch } from 'vue';
  import { MatrixPricingEngine } from '@shared/services/matrixPricingEngine';
  import ToggleInput from '@/components/inputs/ToggleInput.vue';
  import SelectInput from '@/components/inputs/SelectInput.vue';
  import NumberInput from '@/components/inputs/NumberInput.vue';
  import TextInput from '@/components/inputs/TextInput.vue';
  import Btn from '@/components/partials/Btn.vue';
  import {
    choiceAxisLabel,
    choicePriceKey,
    findAxisChoice,
    isChoiceIdAxisValue,
  } from '@shared/utils/choiceAxis';

  export default {
    name: 'MatrixPricingEditor',

    components: {
      ToggleInput,
      SelectInput,
      NumberInput,
      TextInput,
      Btn,
    },

    props: {
      product: {
        type: Object,
        required: true,
      },
      lockDimensions: {
        type: Boolean,
        default: false,
      },
    },

    emits: ['update'],

    setup(props, { emit }) {
      const getStoredColumnValues = (matrix) => {
        const values = matrix?.columnValues ?? matrix?.column_values;
        return Array.isArray(values) ? values : [];
      };

      const choiceAxisValue = (choice) => choicePriceKey(choice) || choice.id;

      const choiceAxisValues = (option) => {
        const seen = new Set();
        const values = [];
        for (const choice of option?.choiceConfig?.choices || []) {
          const key = choiceAxisValue(choice);
          const norm = String(key ?? '').trim().toLowerCase();
          if (!norm || seen.has(norm)) continue;
          seen.add(norm);
          values.push(key);
        }
        return values;
      };

      const axisOption = (optionId) => props.product.options?.find((o) => o.id === optionId);

      const axisLabel = (optionId, value) => choiceAxisLabel(axisOption(optionId), value);

      const formatDimensionValues = (values) =>
        Object.entries(values || {})
          .map(([optionId, value]) => `${getOptionLabel(optionId)}: ${axisLabel(optionId, value)}`)
          .join(', ');

      // A stored row value and a generated one refer to the same choice when they
      // are its id or its label, so prices survive a label → id migration.
      const axisKey = (optionId, value) => {
        if (value === null || value === undefined) return '';
        const choice = findAxisChoice(axisOption(optionId), value);
        return String(choice ? choice.id : value).trim().toLowerCase();
      };

      const importAxisValue = (optionId, value) => {
        const choice = findAxisChoice(axisOption(optionId), value);
        return choice ? choiceAxisValue(choice) : value;
      };

      // Choice-id rows the generator does not produce (an override next to a
      // shared priceKey row) stay in the grid.
      const extraChoiceIdRows = (grid, rowOptionId, groupOptionId, nextGrid) => {
        const option = axisOption(rowOptionId);
        const taken = new Set(
          nextGrid.map((row) => `${axisKey(groupOptionId, row.groupValue)}__${axisKey(rowOptionId, row.rowValue)}`)
        );
        return grid.filter((row) => {
          if (!isChoiceIdAxisValue(option, row?.rowValue)) return false;
          const key = `${axisKey(groupOptionId, row.groupValue)}__${axisKey(rowOptionId, row.rowValue)}`;
          if (taken.has(key)) return false;
          taken.add(key);
          return true;
        });
      };

      const getGeneratedColumnValuesForMatrix = (matrix) => {
        const optionId = matrix?.dimensions?.col;
        if (!optionId) return [];

        const option = props.product.options?.find((o) => o.id === optionId);
        if (!option) return [];

        if (option.type === 'number' && option.numberConfig) {
          const optMin = parseFloat(option.numberConfig.min);
          const max = parseFloat(option.numberConfig.max);
          const steps = matrix?.matrixSteps ?? matrix?.matrix_steps ?? {};
          const starts = matrix?.matrixStarts ?? matrix?.matrix_starts ?? {};
          const step = steps.col ? parseFloat(steps.col) : parseFloat(option.numberConfig.step);
          const startValue =
            starts.col != null && starts.col !== '' && !isNaN(parseFloat(starts.col))
              ? parseFloat(starts.col)
              : null;
          const min =
            startValue != null && startValue >= optMin && startValue <= max ? startValue : optMin;

          if (isNaN(optMin) || isNaN(max) || isNaN(step) || step <= 0 || min > max) {
            return [];
          }

          const values = [];
          const maxIterations = 10000;
          let iterations = 0;
          for (let i = min; i <= max && iterations < maxIterations; i += step) {
            values.push(i);
            iterations++;
          }
          return values;
        }

        if (option.type === 'single-choice' && option.choiceConfig?.choices) {
          return choiceAxisValues(option);
        }

        if (option.type === 'toggle') {
          return [true, false];
        }

        return [];
      };

      const hasManualColumns = (matrix) => {
        const explicit = matrix?.manualColumns ?? matrix?.manual_columns;
        if (explicit != null) return !!explicit;

        const stored = getStoredColumnValues(matrix);
        if (stored.length === 0) return false;

        const generated = getGeneratedColumnValuesForMatrix(matrix);
        return JSON.stringify(stored) !== JSON.stringify(generated);
      };

      const normalizeDeltaTableForEditor = (table) => ({
        ...table,
        useThirdDimension: !!(table?.dimensions?.group || table?.useThirdDimension),
        dimensions: {
          row: table?.dimensions?.row || '',
          col: table?.dimensions?.col || '',
          group: table?.dimensions?.group || '',
        },
        matrixSteps: {
          row: table?.matrixSteps?.row ?? table?.matrix_steps?.row ?? null,
          col: table?.matrixSteps?.col ?? table?.matrix_steps?.col ?? null,
          group: table?.matrixSteps?.group ?? table?.matrix_steps?.group ?? null,
        },
        matrixStarts: {
          row: table?.matrixStarts?.row ?? table?.matrix_starts?.row ?? null,
          col: table?.matrixStarts?.col ?? table?.matrix_starts?.col ?? null,
          group: table?.matrixStarts?.group ?? table?.matrix_starts?.group ?? null,
        },
        columnValues: getStoredColumnValues(table),
        manualColumns: hasManualColumns(table),
        grid: Array.isArray(table?.grid) ? table.grid : [],
      });

      // Create local copies to avoid mutating props
      const useMatrixPricing = ref(props.product.useMatrixPricing || false);
      // Use reactive instead of ref for better v-model support with nested properties
      const dimensions = reactive({
        row: props.product.pricingMatrix?.dimensions?.row || '',
        col: props.product.pricingMatrix?.dimensions?.col || '',
        group: props.product.pricingMatrix?.dimensions?.group || '',
      });
      // Matrix steps - custom step sizes for matrix generation (independent from option step)
      const matrixSteps = reactive({
        row: props.product.pricingMatrix?.matrixSteps?.row || null,
        col: props.product.pricingMatrix?.matrixSteps?.col || null,
        group: props.product.pricingMatrix?.matrixSteps?.group || null,
      });
      // Matrix start - first value in matrix (e.g. 1000); option min can be lower (e.g. 670) for form input
      const matrixStarts = reactive({
        row: props.product.pricingMatrix?.matrixStarts?.row ?? null,
        col: props.product.pricingMatrix?.matrixStarts?.col ?? null,
        group: props.product.pricingMatrix?.matrixStarts?.group ?? null,
      });
      const useThirdDimension = ref(props.product.pricingMatrix?.dimensions?.group ? true : false);
      const priceGrid = ref(props.product.pricingMatrix?.grid || []);
      // Custom (handmatige/geïmporteerde) kolomwaarden. Wanneer `manualColumns`
      // actief is, krijgen deze voorrang op de uniforme generatie uit
      // min/max/step — zo kunnen onregelmatige sprongen (bv. 2500, 3000, 3500,
      // 3650, 4000) bestaan. De runtime-engine rondt sowieso naar boven af naar
      // de eerstvolgende tier, dus die hoeft niet aangepast te worden.
      const customColumnValues = ref(
        getStoredColumnValues(props.product.pricingMatrix)
      );
      const manualColumns = ref(hasManualColumns(props.product.pricingMatrix));
      const deltaTables = ref(
        Array.isArray(props.product.pricingDeltas)
          ? props.product.pricingDeltas.map(normalizeDeltaTableForEditor)
          : []
      );
      const testValues = ref({});
      const testResult = ref(null);
      const testingCalculator = ref(false);
      // Flag + signatures to prevent watcher feedback loops during parent sync.
      let isEmitting = false;
      let lastEmittedMatrixSignature = null;
      let lastEmittedDeltasSignature = null;
      // Watch for prop changes to update local state
      // Only update if value actually changed to prevent recursive updates
      watch(
        () => props.product.useMatrixPricing,
        (newValue, oldValue) => {
          const newVal = newValue || false;
          if (newVal !== useMatrixPricing.value) {
            useMatrixPricing.value = newVal;
          }
        },
        { immediate: true }
      );

      watch(
        () => props.product.pricingMatrix,
        (newMatrix, oldMatrix) => {
          // Skip update if we're currently emitting to prevent circular updates
          if (isEmitting) {
            return;
          }

          // Only update if the matrix actually changed (deep comparison)
          const newMatrixStr = JSON.stringify(newMatrix);
          const oldMatrixStr = JSON.stringify(oldMatrix);
          if (newMatrixStr === oldMatrixStr) {
            return; // No actual change, skip update
          }

          // Ignore parent echo of our own payload to avoid local state rollback.
          if (lastEmittedMatrixSignature && newMatrixStr === lastEmittedMatrixSignature) {
            lastEmittedMatrixSignature = null;
            return;
          }

          if (newMatrix) {
            // Update dimensions only if they changed
            if (newMatrix.dimensions) {
              if (dimensions.row !== (newMatrix.dimensions.row || '')) {
                dimensions.row = newMatrix.dimensions.row || '';
              }
              if (dimensions.col !== (newMatrix.dimensions.col || '')) {
                dimensions.col = newMatrix.dimensions.col || '';
              }
              if (dimensions.group !== (newMatrix.dimensions.group || '')) {
                dimensions.group = newMatrix.dimensions.group || '';
              }
              // Only update useThirdDimension if group dimension is actually set
              // Don't reset it to false if group is null/empty but user just toggled it on
              if (newMatrix.dimensions.group) {
                // If group dimension exists in props, enable third dimension
                if (!useThirdDimension.value) {
                  useThirdDimension.value = true;
                }
              }
              // Note: We don't set useThirdDimension to false here, as the user might have just toggled it on
            }
            // Update matrix steps only if they changed
            if (newMatrix.matrixSteps) {
              if (matrixSteps.row !== (newMatrix.matrixSteps.row || null)) {
                matrixSteps.row = newMatrix.matrixSteps.row || null;
              }
              if (matrixSteps.col !== (newMatrix.matrixSteps.col || null)) {
                matrixSteps.col = newMatrix.matrixSteps.col || null;
              }
              if (matrixSteps.group !== (newMatrix.matrixSteps.group || null)) {
                matrixSteps.group = newMatrix.matrixSteps.group || null;
              }
            }
            // Update matrix starts only if they changed
            if (newMatrix.matrixStarts) {
              if (matrixStarts.row !== (newMatrix.matrixStarts.row ?? null)) {
                matrixStarts.row = newMatrix.matrixStarts.row ?? null;
              }
              if (matrixStarts.col !== (newMatrix.matrixStarts.col ?? null)) {
                matrixStarts.col = newMatrix.matrixStarts.col ?? null;
              }
              if (matrixStarts.group !== (newMatrix.matrixStarts.group ?? null)) {
                matrixStarts.group = newMatrix.matrixStarts.group ?? null;
              }
            }
            // Update grid only if it changed
            if (newMatrix.grid && Array.isArray(newMatrix.grid)) {
              const gridStr = JSON.stringify(newMatrix.grid);
              const currentGridStr = JSON.stringify(priceGrid.value);
              if (gridStr !== currentGridStr) {
                priceGrid.value = newMatrix.grid;
              }
            } else if (priceGrid.value.length > 0) {
              priceGrid.value = [];
            }
            // Sync custom/manual column state from parent
            const nextManual = hasManualColumns(newMatrix);
            if (nextManual !== manualColumns.value) {
              manualColumns.value = nextManual;
            }
            const nextColumnValues = getStoredColumnValues(newMatrix);
            if (nextColumnValues.length > 0) {
              const cvStr = JSON.stringify(nextColumnValues);
              if (cvStr !== JSON.stringify(customColumnValues.value)) {
                customColumnValues.value = [...nextColumnValues];
              }
            }
          } else {
            // Reset if matrix is null
            if (
              dimensions.row ||
              dimensions.col ||
              dimensions.group ||
              useThirdDimension.value ||
              priceGrid.value.length > 0
            ) {
              dimensions.row = '';
              dimensions.col = '';
              dimensions.group = '';
              matrixStarts.row = null;
              matrixStarts.col = null;
              matrixStarts.group = null;
              useThirdDimension.value = false;
              priceGrid.value = [];
              customColumnValues.value = [];
              manualColumns.value = false;
            }
          }
        },
        { deep: true, immediate: true }
      );

      watch(
        () => props.product.pricingDeltas || [],
        (newDeltas) => {
          const newStr = JSON.stringify(newDeltas || []);
          const currentStr = JSON.stringify(deltaTables.value || []);
          if (lastEmittedDeltasSignature && newStr === lastEmittedDeltasSignature) {
            lastEmittedDeltasSignature = null;
            return;
          }
          if (newStr !== currentStr) {
            deltaTables.value = Array.isArray(newDeltas)
              ? newDeltas.map(normalizeDeltaTableForEditor)
              : [];
          }
        },
        { deep: true, immediate: true }
      );

      const createEmptyDeltaTable = () => ({
        id: `delta_table_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        label: '',
        active: true,
        defaultValue: 0,
        useThirdDimension: false,
        dimensions: {
          row: '',
          col: '',
          group: '',
        },
        matrixSteps: {
          row: null,
          col: null,
          group: null,
        },
        matrixStarts: {
          row: null,
          col: null,
          group: null,
        },
        columnValues: [],
        manualColumns: false,
        grid: [],
      });

      const addDeltaTable = () => {
        deltaTables.value.push(createEmptyDeltaTable());
      };

      const removeDeltaTable = (tableIdx) => {
        deltaTables.value.splice(tableIdx, 1);
      };

      const fillEmptyDeltaCells = (tableIdx) => {
        const table = deltaTables.value[tableIdx];
        if (!table?.grid?.length) return;
        let filled = 0;
        table.grid.forEach((row) => {
          row.values.forEach((value, idx) => {
            if (value === null || value === undefined || value === '') {
              const prev = idx > 0 ? row.values[idx - 1] : table.defaultValue || 0;
              row.values[idx] = prev;
              filled++;
            }
          });
        });
        alert(filled > 0 ? `${filled} lege cellen gevuld` : 'Geen lege cellen gevonden');
      };

      const exportDeltaToCsv = (tableIdx) => {
        const table = deltaTables.value[tableIdx];
        if (!table?.grid?.length) {
          alert('Geen data om te exporteren');
          return;
        }

        const colValues = getDeltaColumnValues(table);
        const headers = [];
        if (table.useThirdDimension && table.dimensions.group) {
          headers.push(getOptionLabel(table.dimensions.group));
        }
        headers.push(getOptionLabel(table.dimensions.row));
        headers.push(
          ...colValues.map((v) => (v === null ? 'delta' : axisLabel(table.dimensions.col, v)))
        );

        const rows = table.grid.map((row) => {
          const rowData = [];
          if (table.useThirdDimension && table.dimensions.group) {
            rowData.push(axisLabel(table.dimensions.group, row.groupValue) || '');
          }
          rowData.push(axisLabel(table.dimensions.row, row.rowValue));
          rowData.push(...row.values);
          return rowData;
        });

        const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `delta-table-${table.id || tableIdx + 1}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      };

      const importDeltaCsvText = (tableIdx, text) => {
        const table = deltaTables.value[tableIdx];
        if (!table) return;
        const lines = text.split(/\r?\n/).filter((l) => l.trim());
        if (lines.length < 2) {
          alert('CSV bestand is leeg');
          return;
        }

        const delimiter = detectDelimiter(lines[0]);
        const headers = splitCsvLine(lines[0], delimiter);
        const hasCol = !!table.dimensions?.col;
        const groupOffset = table.useThirdDimension && table.dimensions?.group ? 1 : 0;
        // [groep,] rij [, kolommen...]
        const offset = groupOffset + 1;

        const dataRows = lines
          .slice(1)
          .map((line) => splitCsvLine(line, delimiter))
          .filter((cells) => cells.length >= offset);

        // Kolomwaarden uit de header (alleen relevant als er een kolom-dimensie is).
        const colValues = hasCol
          ? alignHeaderColumns(headers, dataRows, offset)
              .map((h) => parseCsvValue(h, delimiter))
              .filter((v) => v !== '')
              .map((v) => importAxisValue(table.dimensions.col, v))
          : [null];

        if (hasCol && colValues.length === 0) {
          alert('Geen kolomwaarden gevonden in de header van de CSV.');
          return;
        }

        const colCount = hasCol ? colValues.length : 1;
        const importedRows = [];
        dataRows.forEach((cells) => {

          // Lege cel blijft leeg ('' → val terug op defaultValue); anders getal.
          const rawValues = cells.slice(offset).map((v) => {
            if (v === '') return '';
            const n = parseCsvNumber(v, delimiter);
            return Number.isNaN(n) ? '' : n;
          });
          const values = Array.from({ length: colCount }, (_, i) => rawValues[i] ?? '');

          importedRows.push({
            groupValue: groupOffset ? importAxisValue(table.dimensions.group, cells[0]) : null,
            rowValue: importAxisValue(
              table.dimensions.row,
              parseCsvValue(cells[groupOffset], delimiter)
            ),
            values,
          });
        });

        if (importedRows.length === 0) {
          alert('Geen rijen gevonden in de CSV.');
          return;
        }

        if (hasCol) {
          table.columnValues = colValues;
          table.manualColumns = true;
        }
        table.grid = importedRows;
        alert(`${importedRows.length} rijen × ${colCount} kolommen geïmporteerd`);
      };

      const openDeltaCsvImport = (tableIdx) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.csv';
        input.onchange = async (e) => {
          const file = e.target.files[0];
          if (!file) return;
          importDeltaCsvText(tableIdx, await file.text());
        };
        input.click();
      };

      const getDeltaColumnValues = (table) => {
        if (!table?.dimensions?.col) return [null];
        // Custom (geïmporteerde) kolommen krijgen voorrang; anders uniforme generatie.
        if (table?.manualColumns && Array.isArray(table.columnValues) && table.columnValues.length > 0) {
          return table.columnValues;
        }
        return getMatrixDimensionValues(
          table.dimensions.col,
          table?.matrixSteps?.col,
          table?.matrixStarts?.col
        );
      };

      const generateDeltaGrid = (tableIdx) => {
        const table = deltaTables.value[tableIdx];
        if (!table?.dimensions?.row) {
          alert('Selecteer minimaal een rij dimensie voor de delta tabel');
          return;
        }

        if (table.manualColumns) {
          const confirmed = window.confirm(
            'Deze delta tabel heeft aangepaste kolomwaarden (bijv. via CSV import). ' +
              'Opnieuw genereren vervangt deze door gelijkmatige stappen. Doorgaan?'
          );
          if (!confirmed) return;
          table.manualColumns = false;
        }

        const rowValues = getMatrixDimensionValues(
          table.dimensions.row,
          table?.matrixSteps?.row,
          table?.matrixStarts?.row
        );
        const columnValues = table.dimensions.col
          ? getMatrixDimensionValues(
              table.dimensions.col,
              table?.matrixSteps?.col,
              table?.matrixStarts?.col
            )
          : [null];
        const groupValues = table.dimensions.group
          ? getMatrixDimensionValues(
              table.dimensions.group,
              table?.matrixSteps?.group,
              table?.matrixStarts?.group
            )
          : [null];

        if (rowValues.length === 0 || columnValues.length === 0 || groupValues.length === 0) {
          alert('Geen waarden beschikbaar voor de geselecteerde dimensies');
          return;
        }

        table.columnValues = columnValues;
        table.grid = [];
        groupValues.forEach((groupValue) => {
          rowValues.forEach((rowValue) => {
            table.grid.push({
              groupValue,
              rowValue,
              values: columnValues.map(() => 0),
            });
          });
        });
      };

      const getComputedFields = () =>
        props.product.computedFields || props.product.computed_fields || [];

      const getComputedField = (fieldId) =>
        getComputedFields().find((field) => field?.id === fieldId);

      // Infer a numeric range for computed fields from sibling number options
      // (e.g. Hoogte / Geleiderhoogte), so matrix step/start still work.
      const getComputedFieldNumberBounds = () => {
        let min = null;
        let max = null;
        let step = null;

        (props.product.options || []).forEach((option) => {
          if (option?.type !== 'number' || !option.numberConfig) return;
          const optMin = parseFloat(option.numberConfig.min);
          const optMax = parseFloat(option.numberConfig.max);
          const optStep = parseFloat(option.numberConfig.step);
          if (!isNaN(optMin)) min = min == null ? optMin : Math.min(min, optMin);
          if (!isNaN(optMax)) max = max == null ? optMax : Math.max(max, optMax);
          if (!isNaN(optStep) && optStep > 0) {
            step = step == null ? optStep : Math.min(step, optStep);
          }
        });

        return {
          min: min ?? 0,
          max: max ?? 5000,
          step: step ?? 100,
        };
      };

      // Available options + computed fields (computed fields can be matrix axes)
      const availableOptionsList = computed(() => {
        const options = (props.product.options || []).map((opt) => ({
          value: opt.id,
          label: opt.label,
        }));

        const computed = getComputedFields()
          .filter((field) => field?.id && field?.formula)
          .map((field) => ({
            value: field.id,
            label: `${field.label || field.id} (berekend)`,
          }));

        // Put computed fields first so they are easy to find in long option lists
        return [...computed, ...options];
      });

      // Signature of the linked dimension options' min/max/step/choices. When an option's
      // bounds change (e.g. max width 3700 -> 3600), this changes and triggers a resync so
      // the grid prices rows stay consistent with the recomputed column/row values.
      const dimensionOptionsSignature = computed(() => {
        const ids = new Set();
        const addId = (id) => {
          if (id) ids.add(id);
        };
        addId(dimensions.row);
        addId(dimensions.col);
        addId(dimensions.group);
        (deltaTables.value || []).forEach((table) => {
          addId(table?.dimensions?.row);
          addId(table?.dimensions?.col);
          addId(table?.dimensions?.group);
        });

        const options = props.product.options || [];
        const computedFields = getComputedFields();
        return JSON.stringify(
          [...ids].map((id) => {
            const option = options.find((o) => o.id === id);
            if (option) {
              return {
                id,
                min: option?.numberConfig?.min ?? null,
                max: option?.numberConfig?.max ?? null,
                step: option?.numberConfig?.step ?? null,
                choices: option?.choiceConfig?.choices?.map((c) => c.label) ?? null,
              };
            }

            const computed = computedFields.find((field) => field?.id === id);
            if (computed) {
              const bounds = getComputedFieldNumberBounds();
              return {
                id,
                computed: true,
                min: bounds.min,
                max: bounds.max,
                step: bounds.step,
                choices: null,
              };
            }

            return { id };
          })
        );
      });

      // Get option details
      const getOptionLabel = (optionId) => {
        const option = props.product.options?.find((o) => o.id === optionId);
        if (option?.label) return option.label;

        const computed = getComputedField(optionId);
        if (computed?.label) return computed.label;

        return optionId;
      };

      const getOptionType = (optionId) => {
        const option = props.product.options?.find((o) => o.id === optionId);
        if (option?.type) return option.type;
        if (getComputedField(optionId)) return 'number';
        return 'text';
      };

      // Number options and computed fields both drive numeric matrix axes
      const isNumberOption = (optionId) => {
        const option = props.product.options?.find((o) => o.id === optionId);
        if (option?.type === 'number') return true;
        return Boolean(getComputedField(optionId));
      };

      // Get the original step from option config
      const getOptionStep = (optionId) => {
        const option = props.product.options?.find((o) => o.id === optionId);
        if (option?.type === 'number' && option.numberConfig?.step) {
          return String(option.numberConfig.step);
        }
        if (getComputedField(optionId)) {
          return String(getComputedFieldNumberBounds().step);
        }
        return '1';
      };

      // Get the original min from option config (for placeholder / matrix start)
      const getOptionMin = (optionId) => {
        const option = props.product.options?.find((o) => o.id === optionId);
        if (option?.type === 'number' && option.numberConfig?.min != null) {
          return String(option.numberConfig.min);
        }
        if (getComputedField(optionId)) {
          return String(getComputedFieldNumberBounds().min);
        }
        return '';
      };

      const buildNumericDimensionValues = (optMin, max, step, customStart = null) => {
        const startVal =
          customStart != null && customStart !== '' && !isNaN(parseFloat(customStart))
            ? parseFloat(customStart)
            : null;
        const min = startVal != null && startVal >= optMin && startVal <= max ? startVal : optMin;

        if (isNaN(optMin) || isNaN(max) || isNaN(step)) {
          return [];
        }

        if (step <= 0 || min > max) {
          return [];
        }

        const values = [];
        const maxIterations = 10000;
        let iterations = 0;

        for (let i = min; i <= max && iterations < maxIterations; i += step) {
          values.push(i);
          iterations++;
        }

        return values;
      };

      // Generate dimension values with custom matrix step and optional matrix start
      const getMatrixDimensionValues = (optionId, customStep = null, customStart = null) => {
        if (!optionId) return [];

        const option = props.product.options?.find((o) => o.id === optionId);
        if (option) {
          if (option.type === 'number' && option.numberConfig) {
            const optMin = parseFloat(option.numberConfig.min);
            const max = parseFloat(option.numberConfig.max);
            const step = customStep
              ? parseFloat(customStep)
              : parseFloat(option.numberConfig.step);
            return buildNumericDimensionValues(optMin, max, step, customStart);
          }

          if (option.type === 'single-choice' && option.choiceConfig?.choices) {
            return choiceAxisValues(option);
          }

          if (option.type === 'toggle') {
            // Use stable boolean values (not the on/off labels, which are only for
            // display and can change) so matrix cells keep matching.
            return [true, false];
          }

          return [];
        }

        if (getComputedField(optionId)) {
          const bounds = getComputedFieldNumberBounds();
          const step = customStep ? parseFloat(customStep) : bounds.step;
          return buildNumericDimensionValues(bounds.min, bounds.max, step, customStart);
        }

        return [];
      };

      // Preview for matrix dimension values
      const getMatrixDimensionPreview = (optionId, customStep = null, customStart = null) => {
        if (!optionId) return [];
        try {
          return getMatrixDimensionValues(optionId, customStep, customStart)
            .slice(0, 5)
            .map((value) => axisLabel(optionId, value));
        } catch (error) {
          console.error('Error in getMatrixDimensionPreview:', error);
          return [];
        }
      };

      // Generate dimension values based on option type (original, without custom step)
      const getDimensionValues = (optionId) => {
        // ✅ Check of optionId bestaat
        if (!optionId) return [];

        const option = props.product.options?.find((o) => o.id === optionId);
        if (!option) {
          // Computed fields: reuse numeric matrix generation with inferred bounds
          if (getComputedField(optionId)) {
            return getMatrixDimensionValues(optionId);
          }
          return [];
        }

        if (option.type === 'number' && option.numberConfig) {
          // ✅ Parse en valideer min, max, step
          const min = parseFloat(option.numberConfig.min);
          const max = parseFloat(option.numberConfig.max);
          const step = parseFloat(option.numberConfig.step);

          // ✅ Validatie
          if (isNaN(min) || isNaN(max) || isNaN(step)) {
            console.warn('Invalid number config for option:', optionId, option.numberConfig);
            return [];
          }

          if (step <= 0) {
            console.warn('Step must be greater than 0:', step);
            return [];
          }

          if (min > max) {
            console.warn('Min cannot be greater than max:', min, max);
            return [];
          }

          // ✅ Genereer waarden met veilige loop
          const values = [];
          // Voorkom infinite loop door maximum aantal iteraties
          const maxIterations = 10000;
          let iterations = 0;

          for (let i = min; i <= max && iterations < maxIterations; i += step) {
            values.push(i);
            iterations++;
          }

          return values;
        } else if (option.type === 'single-choice' && option.choiceConfig?.choices) {
          return choiceAxisValues(option);
        } else if (option.type === 'toggle') {
          // Stable boolean values, independent of the display labels.
          return [true, false];
        }

        return [];
      };

      const getDimensionPreview = (optionId) => {
        if (!optionId) return [];
        try {
          return getDimensionValues(optionId)
            .slice(0, 5)
            .map((value) => axisLabel(optionId, value));
        } catch (error) {
          console.error('Error in getDimensionPreview:', error);
          return [];
        }
      };

      const getDimensionChoices = (optionId) => {
        const values = getDimensionValues(optionId);
        return values.map((value) => ({ value, label: axisLabel(optionId, value) }));
      };

      // Generate grid using matrix steps
      const generateGrid = () => {
        if (!dimensions.row || !dimensions.col) {
          alert('Selecteer minimaal 2 dimensies');
          return;
        }

        // Custom (geïmporteerde) kolomwaarden worden door uniforme generatie
        // overschreven — vraag eerst om bevestiging zodat een vreemde sprong
        // (bv. 3650) niet ongemerkt verdwijnt.
        if (manualColumns.value) {
          const confirmed = window.confirm(
            'Je hebt aangepaste kolomwaarden (bijv. via CSV import). Opnieuw ' +
              'genereren vervangt deze door gelijkmatige stappen. Doorgaan?'
          );
          if (!confirmed) return;
          manualColumns.value = false;
          customColumnValues.value = [];
        }

        // Use matrix steps and matrix starts if provided, otherwise use option min/step
        const rowValues = getMatrixDimensionValues(dimensions.row, matrixSteps.row, matrixStarts.row);
        const colValues = getMatrixDimensionValues(dimensions.col, matrixSteps.col, matrixStarts.col);
        const groupValues = useThirdDimension.value
          ? getMatrixDimensionValues(dimensions.group, matrixSteps.group, matrixStarts.group)
          : [null];

        if (rowValues.length === 0 || colValues.length === 0) {
          alert('Geen waarden gevonden voor de geselecteerde dimensies');
          return;
        }

        priceGrid.value = [];

        groupValues.forEach((groupValue) => {
          rowValues.forEach((rowValue) => {
            priceGrid.value.push({
              groupValue,
              rowValue,
              prices: colValues.map(() => 0), // Initialize with 0
            });
          });
        });

        // Show success message
        const totalCells = priceGrid.value.length * colValues.length;
        alert(
          `Grid gegenereerd: ${priceGrid.value.length} rijen × ${colValues.length} kolommen = ${totalCells} cellen`
        );
      };

      // Get column values for display. Custom (geïmporteerde/handmatige)
      // kolomwaarden krijgen voorrang op de uniforme generatie, zodat
      // onregelmatige sprongen behouden blijven.
      const getColumnValues = () => {
        if (manualColumns.value && customColumnValues.value.length > 0) {
          return customColumnValues.value;
        }
        if (!dimensions.col) return [];
        return getMatrixDimensionValues(dimensions.col, matrixSteps.col, matrixStarts.col);
      };

      // The form caps a number field at the highest matrix value on its axis,
      // so a matrix max that differs from the option max changes the input range.
      const getAxisRangeIssue = (optionId, values) => {
        const option = axisOption(optionId);
        if (option?.type !== 'number') return null;
        const optionMax = parseFloat(option.numberConfig?.max);
        const numeric = values.map((v) => parseFloat(v)).filter((v) => !isNaN(v));
        if (isNaN(optionMax) || numeric.length === 0) return null;

        const matrixMax = Math.max(...numeric);
        if (matrixMax === optionMax) return null;

        const label = getOptionLabel(optionId);
        return {
          optionId,
          message:
            matrixMax < optionMax
              ? `${label} mag tot ${optionMax}, maar de matrix gaat tot ${matrixMax}. ` +
                `Klanten kunnen in het formulier dan maximaal ${matrixMax} invullen.`
              : `${label} mag tot ${optionMax}, maar de matrix gaat tot ${matrixMax}. ` +
                `Prijzen boven ${optionMax} worden nooit gebruikt.`,
        };
      };

      const getAxisValues = () => ({
        row: priceGrid.value.map((row) => row.rowValue),
        col: getColumnValues(),
      });

      const axisRangeIssues = computed(() => {
        if (!dimensions.row || !dimensions.col || priceGrid.value.length === 0) return [];
        const { row, col } = getAxisValues();
        return [
          getAxisRangeIssue(dimensions.row, row),
          getAxisRangeIssue(dimensions.col, col),
        ].filter(Boolean);
      });

      const canSwapAxes = computed(() => {
        if (axisRangeIssues.value.length === 0) return false;
        if (axisOption(dimensions.row)?.type !== 'number') return false;
        if (axisOption(dimensions.col)?.type !== 'number') return false;
        const { row, col } = getAxisValues();
        return !getAxisRangeIssue(dimensions.col, row) && !getAxisRangeIssue(dimensions.row, col);
      });

      // Swaps which option belongs to rows and columns; the prices stay where they are.
      const swapAxes = () => {
        const columns = [...getColumnValues()];
        [dimensions.row, dimensions.col] = [dimensions.col, dimensions.row];
        [matrixSteps.row, matrixSteps.col] = [matrixSteps.col, matrixSteps.row];
        [matrixStarts.row, matrixStarts.col] = [matrixStarts.col, matrixStarts.row];
        customColumnValues.value = columns;
        manualColumns.value = true;
      };

      // Mark cell as edited (for future features)
      const markCellAsEdited = (rowIdx, colIdx) => {
        // Could be used for tracking changes, validation, etc.
      };

      // Fill empty cells with pattern
      const fillEmptyCells = () => {
        let filled = 0;
        priceGrid.value.forEach((row) => {
          row.prices.forEach((price, idx) => {
            if (!price || price === 0) {
              // Simple pattern: use previous non-zero value or 100
              const prevPrice = idx > 0 ? row.prices[idx - 1] : 100;
              row.prices[idx] = prevPrice;
              filled++;
            }
          });
        });

        if (filled > 0) {
          alert(`${filled} lege cellen gevuld`);
        } else {
          alert('Geen lege cellen gevonden');
        }
      };

      // CSV Export
      const exportToCsv = () => {
        if (priceGrid.value.length === 0) {
          alert('Geen data om te exporteren');
          return;
        }

        const headers = [];
        if (useThirdDimension.value) headers.push(getOptionLabel(dimensions.group));
        headers.push(getOptionLabel(dimensions.row));
        headers.push(...getColumnValues().map((v) => axisLabel(dimensions.col, v)));

        const rows = priceGrid.value.map((row) => {
          const rowData = [];
          if (useThirdDimension.value) {
            rowData.push(axisLabel(dimensions.group, row.groupValue) || '');
          }
          rowData.push(axisLabel(dimensions.row, row.rowValue));
          rowData.push(...row.prices);
          return rowData;
        });

        const csv = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');

        // Download
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `matrix-pricing-${props.product.title || 'product'}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      };

      // CSV Import
      const openCsvImport = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.csv';
        input.onchange = (e) => {
          const file = e.target.files[0];
          if (file) {
            importFromCsv(file);
          }
        };
        input.click();
      };

      // Detecteer het scheidingsteken. Kopiëren vanuit Excel/Sheets geeft
      // tab-gescheiden tekst; Excel-NL exporteert standaard met ';'
      // (en gebruikt ',' als decimaalteken); internationale CSV's gebruiken ','.
      const detectDelimiter = (headerLine) => {
        if (headerLine.includes('\t')) return '\t';
        const semi = (headerLine.match(/;/g) || []).length;
        const comma = (headerLine.match(/,/g) || []).length;
        return semi > comma ? ';' : ',';
      };

      // Parse een CSV-cel naar een getal. Bij ';'-delimiter worden NL-getallen
      // genormaliseerd (punt = duizendtal, komma = decimaal).
      const parseCsvNumber = (raw, delimiter) => {
        if (raw === undefined || raw === null) return NaN;
        let s = String(raw).trim();
        if (s === '') return NaN;
        if (delimiter === ';') {
          s = s.replace(/\./g, '').replace(',', '.');
        }
        const n = parseFloat(s);
        return Number.isNaN(n) ? NaN : n;
      };

      // Header-/rijwaarde: getal waar mogelijk, anders de ruwe (getrimde) tekst
      // (voor choice-dimensies met labels).
      const parseCsvValue = (raw, delimiter) => {
        const n = parseCsvNumber(raw, delimiter);
        return Number.isNaN(n) ? String(raw ?? '').trim() : n;
      };

      // Splits een regel en verwijder lege cellen aan het einde — Excel
      // kopieert vaak extra lege kolommen mee.
      const splitCsvLine = (line, delimiter) => {
        const cells = line.split(delimiter).map((c) => c.trim());
        while (cells.length && cells[cells.length - 1] === '') cells.pop();
        return cells;
      };

      // Bepaal de kolomwaarden uit de kopregel door deze rechts uit te lijnen
      // op het aantal waardecellen in de datarijen. Zo maakt het niet uit of de
      // kop labels of lege hoekcellen vóór de kolomwaarden heeft, of dat hij
      // (zoals in veel Excel-sheets) direct met de kolomwaarden begint.
      const alignHeaderColumns = (headers, dataRows, offset) => {
        const valueCount = Math.max(...dataRows.map((cells) => cells.length - offset), 0);
        return headers.slice(Math.max(headers.length - valueCount, 0));
      };

      const importCsvText = (text) => {
        const lines = text.split(/\r?\n/).filter((l) => l.trim());

        if (lines.length < 2) {
          alert('CSV bestand is leeg');
          return;
        }

        const delimiter = detectDelimiter(lines[0]);
        const headers = splitCsvLine(lines[0], delimiter);
        // Aantal voorloopkolommen vóór de prijzen: [groep,] rij
        const offset = useThirdDimension.value ? 2 : 1;

        const dataRows = lines
          .slice(1)
          .map((line) => splitCsvLine(line, delimiter))
          .filter((cells) => cells.length >= offset + 1);

        // Kolomwaarden = de koppen ná de dimensiekolommen. Deze worden de
        // (mogelijk onregelmatige) breekpunten van de matrix.
        const colValues = alignHeaderColumns(headers, dataRows, offset)
          .map((h) => parseCsvValue(h, delimiter))
          .filter((v) => v !== '')
          .map((v) => importAxisValue(dimensions.col, v));

        if (colValues.length === 0) {
          alert('Geen kolomwaarden gevonden in de header van de CSV.');
          return;
        }

        const newGrid = [];

        dataRows.forEach((cells) => {
          const rawPrices = cells.slice(offset).map((v) => {
            const n = parseCsvNumber(v, delimiter);
            return Number.isNaN(n) ? 0 : n;
          });
          // Houd prijzen index-gelijk met de koppen (pad/truncate).
          const prices = colValues.map((_, i) => rawPrices[i] ?? 0);

          newGrid.push({
            groupValue: useThirdDimension.value ? importAxisValue(dimensions.group, cells[0]) : null,
            rowValue: importAxisValue(dimensions.row, parseCsvValue(cells[offset - 1], delimiter)),
            prices,
          });
        });

        if (newGrid.length === 0) {
          alert('Geen rijen gevonden in de CSV.');
          return;
        }

        // Zet de geïmporteerde koppen als source-of-truth voor de kolommen.
        customColumnValues.value = colValues;
        manualColumns.value = true;
        priceGrid.value = newGrid;
        alert(`${newGrid.length} rijen × ${colValues.length} kolommen geïmporteerd`);
      };

      const importFromCsv = async (file) => {
        importCsvText(await file.text());
      };

      // Parse geplakte data als blok getallen (rijen × cellen) voor plakken
      // vanaf een specifieke cel. Retourneert null als het geen blok is (bijv.
      // een losse waarde), zodat de browser normaal plakt.
      const parseNumberBlock = (text) => {
        const lines = text.split(/\r?\n/).filter((l) => l.trim() !== '');
        if (lines.length === 0) return null;
        // Eén regel zonder tab/';' is een losse waarde (een komma kan daar een
        // NL-decimaal zijn, dus die behandelen we niet als scheidingsteken).
        if (lines.length === 1 && !/[\t;]/.test(lines[0])) return null;
        const delimiter = detectDelimiter(lines[0]);
        return lines.map((line) =>
          line.split(delimiter).map((cell) => {
            const n = parseCsvNumber(cell, delimiter);
            return Number.isNaN(n) ? null : n;
          })
        );
      };

      // Schrijf een blok waarden in een grid vanaf (startRow, startCol), zoals
      // plakken in Excel. Cellen buiten het grid en lege cellen worden overgeslagen.
      const pasteBlockIntoGrid = (grid, valuesKey, startRow, startCol, block) => {
        block.forEach((cells, r) => {
          const row = grid[startRow + r];
          if (!row || !Array.isArray(row[valuesKey])) return;
          cells.forEach((value, c) => {
            if (value === null) return;
            const idx = startCol + c;
            if (idx >= row[valuesKey].length) return;
            row[valuesKey][idx] = value;
          });
        });
      };

      // Plakken (Cmd/Ctrl+V) van CSV of Excel/Sheets-data direct in de editor.
      // Bepaalt op basis van waar de focus staat of het de hoofdmatrix of een
      // delta tabel betreft.
      const handlePaste = (event) => {
        const text = event.clipboardData?.getData('text/plain') || '';

        // Plakken in een prijscel: vul een blok waarden vanaf die cel (één rij,
        // een paar cellen of meerdere rijen — zonder kopregel).
        const cellInput = event.target;
        if (cellInput?.classList?.contains('price-input') && cellInput.dataset?.row != null) {
          const block = parseNumberBlock(text);
          if (!block) return; // losse waarde → normaal plakken in de cel
          event.preventDefault();
          const rowIdx = Number(cellInput.dataset.row);
          const colIdx = Number(cellInput.dataset.col);
          if (cellInput.dataset.table != null) {
            const table = deltaTables.value[Number(cellInput.dataset.table)];
            if (table?.grid) pasteBlockIntoGrid(table.grid, 'values', rowIdx, colIdx, block);
          } else {
            pasteBlockIntoGrid(priceGrid.value, 'prices', rowIdx, colIdx, block);
          }
          return;
        }
        // Alleen ingrijpen bij tabel-achtige data (meerdere regels met een
        // scheidingsteken); een enkel getal moet gewoon in de cel geplakt worden.
        const lines = text.split(/\r?\n/).filter((l) => l.trim());
        if (lines.length < 2 || !/[\t;,]/.test(lines[0])) return;

        // Focus binnen een delta tabel? Dan die tabel vullen.
        const deltaCard = event.target?.closest?.('.delta-card');
        let deltaIdx = -1;
        if (deltaCard) {
          const cards = Array.from(
            deltaCard.parentElement?.querySelectorAll?.(':scope > .delta-card') || []
          );
          deltaIdx = cards.indexOf(deltaCard);
        }

        if (deltaIdx >= 0 && deltaTables.value[deltaIdx]) {
          if (!deltaTables.value[deltaIdx].dimensions?.row) {
            alert('Selecteer eerst een rij dimensie voor deze delta tabel.');
            return;
          }
          event.preventDefault();
          importDeltaCsvText(deltaIdx, text);
          return;
        }

        if (!dimensions.row || !dimensions.col) {
          alert('Selecteer eerst een rij- en kolomdimensie voordat je data plakt.');
          return;
        }
        event.preventDefault();
        importCsvText(text);
      };

      // Test calculator
      const calculateTestPrice = () => {
        const engine = new MatrixPricingEngine();

        // Build test configuration
        const testConfig = {};
        if (useThirdDimension.value && testValues.value[dimensions.group]) {
          testConfig[`${props.product.id}-${dimensions.group}-input`] =
            testValues.value[dimensions.group];
        }
        testConfig[`${props.product.id}-${dimensions.row}-input`] =
          testValues.value[dimensions.row];
        testConfig[`${props.product.id}-${dimensions.col}-input`] =
          testValues.value[dimensions.col];

        // Convert grid to matrix format for engine
        const matrixCells = [];
        const columnValues = getColumnValues();

        priceGrid.value.forEach((row) => {
          row.prices.forEach((price, colIdx) => {
            if (price > 0) {
              const cell = {
                dimensions: {
                  [dimensions.row]: row.rowValue,
                  [dimensions.col]: columnValues[colIdx],
                },
                price: price,
                available: true,
              };

              if (useThirdDimension.value && row.groupValue) {
                cell.dimensions[dimensions.group] = row.groupValue;
              }

              matrixCells.push(cell);
            }
          });
        });

        testResult.value = engine.calculatePrice(
          {
            ...props.product,
            useMatrixPricing: useMatrixPricing.value,
            pricingMatrix: {
              dimensions: {
                row: dimensions.row,
                col: dimensions.col,
                group: useThirdDimension.value ? dimensions.group : null,
              },
              grid: priceGrid.value,
              columnValues: columnValues,
              cells: matrixCells,
            },
          },
          testConfig
        );
      };

      // Resize a prices/values row to the expected column count: truncate extra
      // trailing columns, pad missing ones with 0.
      const resizeRowValues = (values, columnCount) => {
        const arr = Array.isArray(values) ? values.slice(0, columnCount) : [];
        while (arr.length < columnCount) arr.push(0);
        return arr;
      };

      // Reconcile the main price grid with the current dimension values. Keeps:
      // - each row's prices length === column_values length
      // - the number of rows in sync with the expected row (× group) values
      // Existing prices are preserved (matched on group/row value) where possible.
      const reconcileMainGrid = () => {
        if (!Array.isArray(priceGrid.value) || priceGrid.value.length === 0) return;
        if (!dimensions.row || !dimensions.col) return;

        const colValues = getColumnValues();
        // Don't destroy data on a transient/invalid config (no resolvable columns).
        if (colValues.length === 0) return;
        const colCount = colValues.length;

        // Bij custom (geïmporteerde) kolommen NIET herbouwen uit de uniforme
        // rij-/kolomgeneratie — dat zou de onregelmatige breekpunten en de
        // geïmporteerde rijen wissen. Houd de grid intact en zorg alleen dat
        // elke rij exact `colCount` prijzen heeft (invariant prices.length ===
        // columnValues.length).
        if (manualColumns.value) {
          const nextGrid = priceGrid.value.map((row) => ({
            groupValue: row?.groupValue ?? null,
            rowValue: row?.rowValue ?? null,
            prices: resizeRowValues(row?.prices, colCount),
          }));
          if (JSON.stringify(nextGrid) !== JSON.stringify(priceGrid.value)) {
            priceGrid.value = nextGrid;
          }
          return;
        }

        const rowValues = getMatrixDimensionValues(
          dimensions.row,
          matrixSteps.row,
          matrixStarts.row
        );
        const groupValues =
          useThirdDimension.value && dimensions.group
            ? getMatrixDimensionValues(dimensions.group, matrixSteps.group, matrixStarts.group)
            : [null];

        const groupDim = useThirdDimension.value ? dimensions.group : null;
        const rowKey = (groupValue, rowValue) =>
          `${axisKey(groupDim, groupValue)}__${axisKey(dimensions.row, rowValue)}`;
        const existing = new Map();
        priceGrid.value.forEach((row) => {
          const key = rowKey(row?.groupValue, row?.rowValue);
          if (!existing.has(key)) existing.set(key, row);
        });

        let nextGrid;
        if (rowValues.length === 0) {
          // Can't resolve row values; only fix the column dimension of current rows.
          nextGrid = priceGrid.value.map((row) => ({
            groupValue: row?.groupValue ?? null,
            rowValue: row?.rowValue ?? null,
            prices: resizeRowValues(row?.prices, colCount),
          }));
        } else {
          nextGrid = [];
          groupValues.forEach((groupValue) => {
            rowValues.forEach((rowValue) => {
              const prev = existing.get(rowKey(groupValue, rowValue));
              nextGrid.push({
                groupValue,
                rowValue,
                prices: resizeRowValues(prev?.prices, colCount),
              });
            });
          });
          extraChoiceIdRows(priceGrid.value, dimensions.row, groupDim, nextGrid).forEach((row) =>
            nextGrid.push({
              groupValue: row.groupValue ?? null,
              rowValue: row.rowValue,
              prices: resizeRowValues(row.prices, colCount),
            })
          );
        }

        if (JSON.stringify(nextGrid) !== JSON.stringify(priceGrid.value)) {
          priceGrid.value = nextGrid;
        }
      };

      // Reconcile a single delta table grid (and its columnValues) with its dimensions.
      const reconcileDeltaTable = (table) => {
        if (!table || !Array.isArray(table.grid) || table.grid.length === 0) return;
        if (!table?.dimensions?.row) return;

        const hasCol = !!table.dimensions.col;

        // Bij custom (geïmporteerde) kolommen: grid + breekpunten intact houden,
        // alleen elke rij op de juiste lengte brengen. columnValues NIET
        // overschrijven met uniforme generatie.
        if (table.manualColumns && hasCol) {
          const colValues = getDeltaColumnValues(table);
          if (colValues.length === 0) return;
          const colCount = colValues.length;
          const nextGrid = table.grid.map((row) => ({
            groupValue: row?.groupValue ?? null,
            rowValue: row?.rowValue ?? null,
            values: resizeRowValues(row?.values, colCount),
          }));
          if (JSON.stringify(nextGrid) !== JSON.stringify(table.grid)) {
            table.grid = nextGrid;
          }
          return;
        }

        const colValues = hasCol
          ? getMatrixDimensionValues(
              table.dimensions.col,
              table?.matrixSteps?.col,
              table?.matrixStarts?.col
            )
          : [null];
        if (hasCol && colValues.length === 0) return;
        const colCount = colValues.length;

        const rowValues = getMatrixDimensionValues(
          table.dimensions.row,
          table?.matrixSteps?.row,
          table?.matrixStarts?.row
        );
        const groupValues =
          table.useThirdDimension && table.dimensions.group
            ? getMatrixDimensionValues(
                table.dimensions.group,
                table?.matrixSteps?.group,
                table?.matrixStarts?.group
              )
            : [null];

        const groupDim = table.useThirdDimension ? table.dimensions.group : null;
        const rowKey = (groupValue, rowValue) =>
          `${axisKey(groupDim, groupValue)}__${axisKey(table.dimensions.row, rowValue)}`;
        const existing = new Map();
        table.grid.forEach((row) => {
          const key = rowKey(row?.groupValue, row?.rowValue);
          if (!existing.has(key)) existing.set(key, row);
        });

        let nextGrid;
        if (rowValues.length === 0) {
          nextGrid = table.grid.map((row) => ({
            groupValue: row?.groupValue ?? null,
            rowValue: row?.rowValue ?? null,
            values: resizeRowValues(row?.values, colCount),
          }));
        } else {
          nextGrid = [];
          groupValues.forEach((groupValue) => {
            rowValues.forEach((rowValue) => {
              const prev = existing.get(rowKey(groupValue, rowValue));
              nextGrid.push({
                groupValue,
                rowValue,
                values: resizeRowValues(prev?.values, colCount),
              });
            });
          });
          extraChoiceIdRows(table.grid, table.dimensions.row, groupDim, nextGrid).forEach((row) =>
            nextGrid.push({
              groupValue: row.groupValue ?? null,
              rowValue: row.rowValue,
              values: resizeRowValues(row.values, colCount),
            })
          );
        }

        if (JSON.stringify(nextGrid) !== JSON.stringify(table.grid)) {
          table.grid = nextGrid;
        }
        if (JSON.stringify(colValues) !== JSON.stringify(table.columnValues || [])) {
          table.columnValues = colValues;
        }
      };

      // Bring every grid back in line with the current dimension settings before emitting.
      const reconcileGrids = () => {
        reconcileMainGrid();
        (deltaTables.value || []).forEach((table) => reconcileDeltaTable(table));
      };

      // Auto-emit changes to parent with debouncing
      let emitTimeout = null;
      const serializeDeltaTables = () =>
        (deltaTables.value || []).map((table) => ({
          id: table.id,
          label: table.label,
          active: table.active !== false,
          defaultValue: Number(table.defaultValue || 0),
          dimensions: {
            row: table?.dimensions?.row || '',
            col: table?.dimensions?.col || '',
            group: table?.useThirdDimension ? table?.dimensions?.group || '' : '',
          },
          matrixSteps: {
            row: table?.matrixSteps?.row ?? null,
            col: table?.matrixSteps?.col ?? null,
            group: table?.useThirdDimension ? table?.matrixSteps?.group ?? null : null,
          },
          matrixStarts: {
            row: table?.matrixStarts?.row ?? null,
            col: table?.matrixStarts?.col ?? null,
            group: table?.useThirdDimension ? table?.matrixStarts?.group ?? null : null,
          },
          columnValues: Array.isArray(table?.columnValues) ? table.columnValues : [],
          manualColumns: !!table?.manualColumns,
          grid: Array.isArray(table?.grid)
            ? table.grid.map((row) => ({
                groupValue: table?.useThirdDimension ? row.groupValue ?? null : null,
                rowValue: row.rowValue,
                values: Array.isArray(row.values) ? row.values : [],
              }))
            : [],
        }));

      const emitChanges = () => {
        if (emitTimeout) clearTimeout(emitTimeout);
        emitTimeout = setTimeout(() => {
          // Ensure column_values and every grid prices row stay consistent before
          // serializing, so the backend invariant (prices.length === column_values.length)
          // can never be violated by a stale grid.
          reconcileGrids();
          isEmitting = true;
          const serializedDeltas = serializeDeltaTables();
          const nextPricingMatrix = {
            dimensions: {
              row: dimensions.row,
              col: dimensions.col,
              // Only set group to null if useThirdDimension is false
              // If useThirdDimension is true but group is empty, keep it as empty string
              group: useThirdDimension.value ? (dimensions.group || '') : null,
            },
            matrixSteps: {
              row: matrixSteps.row || null,
              col: matrixSteps.col || null,
              group: useThirdDimension.value ? (matrixSteps.group || null) : null,
            },
            matrixStarts: {
              row: matrixStarts.row ?? null,
              col: matrixStarts.col ?? null,
              group: useThirdDimension.value ? (matrixStarts.group ?? null) : null,
            },
            grid: priceGrid.value,
            columnValues: getColumnValues(),
            manualColumns: manualColumns.value,
          };
          lastEmittedMatrixSignature = JSON.stringify(nextPricingMatrix);
          lastEmittedDeltasSignature = JSON.stringify(serializedDeltas);
          emit('update', {
            useMatrixPricing: useMatrixPricing.value,
            pricingMatrix: nextPricingMatrix,
            pricingDeltas: serializedDeltas,
          });
          // Reset flag after a short delay to allow parent update to propagate
          setTimeout(() => {
            isEmitting = false;
          }, 100);
        }, 300);
      };

      // Watch all relevant state changes and auto-emit
      watch(
        [
          useMatrixPricing,
          () => dimensions.row,
          () => dimensions.col,
          () => dimensions.group,
          () => matrixSteps.row,
          () => matrixSteps.col,
          () => matrixSteps.group,
          () => matrixStarts.row,
          () => matrixStarts.col,
          () => matrixStarts.group,
          useThirdDimension,
          priceGrid,
          deltaTables,
          dimensionOptionsSignature,
          manualColumns,
          customColumnValues,
        ],
        () => {
          emitChanges();
        },
        { deep: true }
      );

      return {
        useMatrixPricing,
        dimensions,
        matrixSteps,
        matrixStarts,
        useThirdDimension,
        priceGrid,
        deltaTables,
        testValues,
        testResult,
        availableOptionsList,
        getOptionLabel,
        getOptionType,
        isNumberOption,
        getOptionStep,
        getOptionMin,
        getDimensionValues,
        getDimensionPreview,
        getDimensionChoices,
        axisLabel,
        formatDimensionValues,
        getMatrixDimensionValues,
        getMatrixDimensionPreview,
        generateGrid,
        getColumnValues,
        manualColumns,
        axisRangeIssues,
        canSwapAxes,
        swapAxes,
        markCellAsEdited,
        fillEmptyCells,
        exportToCsv,
        openCsvImport,
        handlePaste,
        addDeltaTable,
        removeDeltaTable,
        generateDeltaGrid,
        getDeltaColumnValues,
        fillEmptyDeltaCells,
        exportDeltaToCsv,
        openDeltaCsvImport,
        calculateTestPrice,
        testingCalculator,
      };
    },
  };
</script>

<style scoped lang="scss">
  .matrix-dimensions--locked {
    pointer-events: none;
    opacity: 0.7;
  }

  .matrix-pricing-editor {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    outline: none;
    &__test-calculator {
      text-decoration: underline;
      cursor: pointer;
      color: var(--brand-500);
    }
  }

  .matrix-content {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .axis-range-warning {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
    padding: 1rem 1.25rem;
    border: 1px solid var(--amber-200);
    border-radius: 0.5rem;
    background: var(--amber-50);
    color: var(--amber-800);
    font-size: 0.875rem;

    ul,
    p {
      margin: 0;
    }

    ul {
      padding-left: 1.25rem;
    }
  }

  .card {
    background: white;
    border: 1px solid var(--slate-200);
    border-radius: 0.5rem;
    padding: 1.5rem;
  }

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;

    h4 {
      margin: 0;
      font-size: 1.125rem;
      font-weight: 600;
    }
  }

  .grid-actions {
    display: flex;
    gap: 0.5rem;
    align-items: center;
  }

  .text-muted {
    color: var(--slate-600);
    font-size: 0.875rem;
  }

  .mb-3 {
    margin-bottom: 0.75rem;
  }

  .mb-4 {
    margin-bottom: 1rem;
  }

  .mt-3 {
    margin-top: 0.75rem;
  }

  .mt-4 {
    margin-top: 1rem;
  }

  .dimension-setup {
    h4 {
      margin: 0 0 1rem 0;
      font-size: 1.125rem;
      font-weight: 600;
    }
    background-color: var(--slate-50);
    border: 1px solid var(--slate-200);
    border-radius: 0.5rem;
    padding: 1.5rem;
  }

  .dimension-selectors {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;
    margin-bottom: 1.5rem;
  }

  .dimension-selector {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .dimension-preview {
    padding: 0.5rem;
    background: var(--slate-50);
    border-radius: 0.25rem;
    font-size: 0.75rem;
    color: var(--slate-600);
  }

  .third-dimension {
    background-color: var(--slate-50);
    border: 1px solid var(--slate-200);
    border-radius: 0.5rem;
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .grid-actions-section {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem;
    background: var(--slate-50);
    border: 1px solid var(--slate-200);
    border-radius: 0.5rem;
  }

  .grid-stats {
    font-size: 0.875rem;
    color: var(--slate-600);
    margin-left: auto;
  }

  .paste-hint {
    font-size: 0.8125rem;
  }

  .matrix-table-wrapper {
    overflow-x: auto;
  }

  .card--price-matrix {
    background-color: var(--slate-50);
    border: 1px solid var(--slate-200);
    border-radius: 0.5rem;
    padding: 0;
    .section-header {
      padding: 1.5rem;
      margin-bottom: 0;
    }
  }

  .matrix-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.75rem;

    th {
      background: var(--slate-50);
      padding: 0.25rem 0.375rem;
      text-align: left;
      font-weight: 600;
      font-size: 0.6875rem;
      border: 1px solid var(--slate-200);
      &:first-child {
        border-left: 0;
      }
      &:last-child {
        border-right: 0;
      }
    }

    td {
      padding: 0.25rem 0.375rem;
      border: 1px solid var(--slate-200);
      text-align: center;
      &:first-child {
        border-left: 0;
      }
      &:last-child {
        border-right: 0;
      }
    }

    .col-header {
      text-align: center;
      font-weight: 600;
    }

    .group-cell {
      background: var(--slate-50);
      font-weight: 500;
    }

    .row-cell {
      background: var(--slate-50);
      font-weight: 500;
    }

    .price-cell {
      padding: 0;
      font-family: 'JetBrains Mono', monospace;
      background-color: #ffffff;
      color: var(--slate-500);
      font-weight: 500;
      text-align: center;
      vertical-align: middle;
    }
  }

  .price-input {
    width: 100%;
    padding: 0.125rem;
    border: none;
    border-radius: 0;
    font-size: 0.6875rem;
    text-align: center;
    height: 100%;
    // Verberg de number-spinners zodat de cellen compact blijven.
    appearance: textfield;
    -moz-appearance: textfield;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    &:focus {
      outline: none;
      border-color: var(--brand-500);
    }
  }

  .grid-help {
    margin-top: 1rem;
    padding: 0.75rem;
    background: var(--blue-50);
    border-radius: 0.375rem;
    border: 1px solid var(--blue-200);
  }

  .delta-section {
    background-color: var(--slate-50);
    border: 1px solid var(--slate-200);
  }

  .delta-empty {
    padding: 0.75rem 0;
  }

  .delta-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-top: 1rem;
  }

  .delta-card {
    border: 1px solid var(--slate-200);
    border-radius: 0.5rem;
    padding: 1.5rem;
    background: #fff;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .delta-card__meta {
    display: grid;
    grid-template-columns: minmax(280px, 1fr) minmax(180px, 220px);
    gap: 0.75rem;
    align-items: end;
  }

  .delta-table {
    .price-input {
      min-width: 56px;
    }
  }

  .test-inputs {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
  }

  .test-result {
    padding: 1rem;
    border-radius: 0.375rem;
  }

  .result-success {
    background: var(--green-50);
    border: 1px solid var(--green-200);
    padding: 1rem;
    border-radius: 0.375rem;

    h5 {
      margin: 0 0 0.5rem 0;
      color: var(--green-800);
    }
  }

  .result-price {
    font-size: 2rem;
    font-weight: 700;
    color: var(--green-700);
    margin: 0.5rem 0;
  }

  .tier-adjustments {
    margin-top: 0.75rem;
    font-size: 0.875rem;
    color: var(--green-800);

    ul {
      margin: 0.5rem 0 0 1.5rem;
      padding: 0;
    }
  }

  .matched-cell {
    margin-top: 0.75rem;
    font-size: 0.875rem;

    code {
      display: block;
      margin-top: 0.25rem;
      padding: 0.5rem;
      background: white;
      border-radius: 0.25rem;
      font-family: monospace;
    }
  }

  .result-error {
    background: var(--red-50);
    border: 1px solid var(--red-200);
    padding: 1rem;
    border-radius: 0.375rem;

    h5 {
      margin: 0 0 0.5rem 0;
      color: var(--red-800);
    }

    p {
      color: var(--red-700);
      margin: 0.5rem 0;
    }
  }

  .tried-values {
    margin-top: 0.75rem;
    font-size: 0.875rem;
    color: var(--red-800);

    code {
      display: block;
      margin-top: 0.25rem;
      padding: 0.5rem;
      background: white;
      border-radius: 0.25rem;
      font-family: monospace;
    }
  }
</style>
